import { useEffect, useState } from "preact/hooks";
import { signal } from "@preact/signals";
import { conversion } from "./analytics.ts";
import { GroupsPage } from "./groupsPage.tsx";
import { ConversationsView } from "./convoPage.tsx";
import { ConversationComponent } from "./conversation.tsx";
import { auth, sessionSig, useAuth } from "./db.ts";
import { LandingPage } from "./landing.tsx";
import { PrivacyPolicy, TermsOfService } from "./legal.tsx";
import { AdminPanel } from "./admin.tsx";
import { DocsPage } from "./docs.tsx";
import { SnapshotPage } from "./snapshotPage.tsx";
import { SnapshotsView } from "./snapshots.tsx";
import { CheckupsPage } from "./checkups.tsx";
import { ReportsPage } from "./reports.tsx";
import { SettingsPage } from "./userTokens.tsx";
import { ComponentType } from "preact";
import { useLocation } from "preact-iso";
import {
  ErrorMessage,
  obfuscateIdsSignal,
  PageContainer,
  useIsMobile,
} from "./uiComponents.tsx";
import { ClipLoader } from "react-spinners";
import { toast } from "react-hot-toast";
import { notifyError } from "./notify.ts";
import { primaryColor, t } from "./theme.ts";
import { Moon, Sun } from "lucide-preact";

// Theme signal for dark/light mode
export const isDarkMode = signal(true);

// Effect to apply theme to document body
export const useTheme = () => {
  useEffect(() => {
    if (typeof document !== "undefined") {
      if (isDarkMode.value) {
        document.body.classList.add("dark");
        document.body.classList.remove("light");
      } else {
        document.body.classList.add("light");
        document.body.classList.remove("dark");
      }
    }
  }, [isDarkMode.value]);
};

const ThemeToggle = () => (
  <button
    type="button"
    onClick={() => isDarkMode.value = !isDarkMode.value}
    style={{
      background: "transparent",
      border: "none",
      color: t().textDim,
      cursor: "pointer",
      padding: "4px 8px",
      display: "flex",
      alignItems: "center",
      transition: "color 0.2s ease",
    }}
    title={isDarkMode.value ? "Switch to light mode" : "Switch to dark mode"}
    onMouseEnter={(e) => {
      e.currentTarget.style.color = t().textMuted;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.color = t().textDim;
    }}
  >
    {isDarkMode.value ? <Sun size={16} /> : <Moon size={16} />}
  </button>
);

const getInputStyle = () => ({
  padding: "12px 16px",
  borderRadius: 8,
  border: `1px solid ${t().inputBorder}`,
  fontSize: 15,
  outline: "none",
  background: t().inputBg,
  color: t().text,
  transition: "border-color 0.15s ease",
  width: "100%",
  boxSizing: "border-box" as const,
});

const getButtonStyle = () => ({
  background: primaryColor(),
  color: "white",
  border: "none",
  borderRadius: 8,
  padding: "12px 0",
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
  transition: "background 0.15s ease",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  width: "100%",
});

const Email = ({ setSentEmail }: { setSentEmail: (email: string) => void }) => {
  const [email, setEmail] = useState("");

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!email) return;
    conversion(email);
    setSentEmail(email);
    auth.sendMagicCode({ email }).catch((err) => {
      notifyError(err, "Sign in failed");
      setSentEmail("");
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <input
        placeholder="Enter your email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
        style={getInputStyle()}
      />
      <button type="submit" style={getButtonStyle()}>
        Continue with Email
      </button>
    </form>
  );
};

const MagicCode = ({ sentEmail }: { sentEmail: string }) => {
  const [code, setCode] = useState("");

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    auth.signInWithMagicCode({ email: sentEmail, code }).catch((err) => {
      notifyError(err, "Verification failed");
      setCode("");
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <h2
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: t().text,
            marginBottom: 6,
          }}
        >
          Check your email
        </h2>
        <div style={{ fontSize: 14, color: t().textMuted }}>
          We sent a temporary code to <strong>{sentEmail}</strong>.
        </div>
      </div>
      <input
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="Enter 6-digit code"
        value={code}
        // @ts-expect-error preact issue
        onChange={(e) => setCode(e.target.value)}
        style={{
          ...getInputStyle(),
          textAlign: "center",
          letterSpacing: 4,
          fontSize: 18,
          fontWeight: 600,
        }}
      />
      <button type="submit" style={getButtonStyle()}>
        Verify Code
      </button>
    </form>
  );
};

const GoogleIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 48 48"
    style={{ marginRight: 4 }}
  >
    <g>
      <path
        fill="#4285F4"
        d="M24 9.5c3.54 0 6.73 1.22 9.24 3.22l6.9-6.9C35.64 2.34 30.13 0 24 0 14.82 0 6.73 5.48 2.69 13.44l8.06 6.27C12.6 13.16 17.87 9.5 24 9.5z"
      />
      <path
        fill="#34A853"
        d="M46.1 24.5c0-1.64-.15-3.22-.43-4.75H24v9.02h12.44c-.54 2.9-2.18 5.36-4.64 7.02l7.18 5.59C43.98 37.36 46.1 31.44 46.1 24.5z"
      />
      <path
        fill="#FBBC05"
        d="M10.75 28.71c-1.13-3.36-1.13-6.97 0-10.33l-8.06-6.27C.86 16.36 0 20.06 0 24c0 3.94.86 7.64 2.69 11.09l8.06-6.27z"
      />
      <path
        fill="#EA4335"
        d="M24 48c6.13 0 11.64-2.02 15.92-5.5l-7.18-5.59c-2.01 1.35-4.59 2.15-7.74 2.15-6.13 0-11.4-3.66-13.25-8.71l-8.06 6.27C6.73 42.52 14.82 48 24 48z"
      />
      <path fill="none" d="M0 0h48v48H0z" />
    </g>
  </svg>
);

const googleButtonStyle = () => ({
  background: isDarkMode.value ? "rgba(255, 255, 255, 0.08)" : "#ffffff",
  color: isDarkMode.value ? "#ffffff" : "#1f2937",
  border: isDarkMode.value
    ? "1px solid rgba(255, 255, 255, 0.15)"
    : "1px solid #d1d5db",
  borderRadius: 8,
  padding: "12px 16px",
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
  transition: "all 0.15s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  textDecoration: "none",
  width: "100%",
  boxSizing: "border-box" as const,
  marginBottom: 16,
});

const Login = () => {
  const [sentEmail, setSentEmail] = useState("");
  const route = useLocation().route;

  const googleAuthUrl = auth.createAuthorizationURL({
    clientName: "google-web",
    redirectURL: globalThis.location.href,
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: t().pageBg,
        overflow: "auto",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: t().cardBg,
          padding: "40px 32px",
          borderRadius: 14,
          boxShadow: "0 4px 32px rgba(45,140,255,0.10)",
          color: t().text,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            userSelect: "none",
            marginBottom: 28,
          }}
          onClick={() => route("/")}
        >
          <img
            src="/favicon.svg"
            alt="ViewChat Logo"
            style={{ width: 28, height: 28 }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: 24,
              letterSpacing: 1,
              color: primaryColor(),
            }}
          >
            ViewChat
          </span>
        </div>
        <a href={googleAuthUrl} style={googleButtonStyle()}>
          <GoogleIcon />
          Log in with Google
        </a>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: "20px 0",
          }}
        >
          <div style={{ flex: 1, height: 1, background: t().borderLight }} />
          <span
            style={{
              margin: "0 12px",
              color: t().textMuted,
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: 1,
            }}
          >
            OR
          </span>
          <div style={{ flex: 1, height: 1, background: t().borderLight }} />
        </div>
        {!sentEmail
          ? <Email setSentEmail={setSentEmail} />
          : <MagicCode sentEmail={sentEmail} />}
      </div>
    </div>
  );
};

const _menuButtonStyle = {
  cursor: "pointer",
};

const Header = () => {
  const route = useLocation().route;
  const currentPath = useLocation().path;
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const { user } = useAuth();
  useEffect(() => {
    sessionSig.value = user || null;
  }, [user]);

  const siteName = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={() => route("/")}
    >
      <span
        style={{
          fontWeight: 600,
          fontSize: 18,
          color: primaryColor(),
        }}
      >
        ViewChat
      </span>
    </div>
  );

  const navLinks = (
    <div
      style={{
        display: isMobile ? "flex" : "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 4,
        alignItems: isMobile ? undefined : "center",
      }}
    >
      {routes.filter((x) => x.text).map(({ path, text }) => (
        <button
          type="button"
          key={path}
          onClick={() => {
            route(path);
            if (isMobile) setMenuOpen(false);
          }}
          style={{
            background: "transparent",
            color: currentPath === path ? t().textBright : t().textDim,
            border: "none",
            borderRadius: 4,
            padding: "4px 10px",
            fontSize: 14,
            fontWeight: 400,
            cursor: "pointer",
            transition: "color 0.2s ease",
            margin: isMobile ? "2px 0" : undefined,
            width: isMobile ? "100%" : undefined,
            textAlign: isMobile ? "left" : "center",
          }}
          onMouseEnter={(e) => {
            if (currentPath !== path) {
              e.currentTarget.style.color = t().textMuted;
            }
          }}
          onMouseLeave={(e) => {
            if (currentPath !== path) {
              e.currentTarget.style.color = t().textDim;
            }
          }}
        >
          {text}
        </button>
      ))}
    </div>
  );

  const userSection = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginLeft: "auto",
      }}
    >
      {!isMobile && sessionSig.value?.email && (
        <>
          <div
            style={{
              fontSize: 12,
              color: t().textFaint,
            }}
          >
            {sessionSig.value.email}
          </div>
          <button
            type="button"
            onClick={() => {
              auth.signOut();
              route("/");
            }}
            style={{
              background: "transparent",
              color: t().textDim,
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              fontSize: 14,
              fontWeight: 400,
              cursor: "pointer",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = t().textMuted;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = t().textDim;
            }}
          >
            Sign out
          </button>
        </>
      )}
      <ThemeToggle />
    </div>
  );

  return (
    <div
      style={{
        gap: 10,
        padding: "8px 12px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "relative",
        borderBottom: `1px solid ${t().border}`,
      }}
    >
      {siteName}
      {isMobile && (
        <>
          <button
            type="button"
            aria-label="Open navigation menu"
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              background: "transparent",
              color: t().textDim,
              border: "none",
              borderRadius: 4,
              padding: "6px 10px",
              fontSize: 16,
              cursor: "pointer",
              transition: "color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = t().textMuted;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = t().textDim;
            }}
          >
            ☰
          </button>
          {menuOpen && (
            <>
              {/* Backdrop */}
              <div
                onClick={() => setMenuOpen(false)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0,0,0,0.5)",
                  zIndex: 99,
                }}
              />
              {/* Menu panel */}
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: "280px",
                  height: "100vh",
                  background: t().surfaceBg,
                  zIndex: 100,
                  padding: "16px",
                  borderLeft: `1px solid ${t().border}`,
                  boxShadow: "-4px 0 20px rgba(0,0,0,0.5)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Close button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 16,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setMenuOpen(false)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: t().textMuted,
                      fontSize: 24,
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Navigation Section */}
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: t().textDim,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 12,
                      paddingLeft: 4,
                    }}
                  >
                    Navigation
                  </div>
                  {routes.filter((x) => x.text).map(({ path, text }) => (
                    <button
                      type="button"
                      key={path}
                      onClick={() => {
                        route(path);
                        setMenuOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        background: "transparent",
                        color: currentPath === path
                          ? t().textBright
                          : t().textMuted,
                        border: "none",
                        borderRadius: 4,
                        padding: "12px 10px",
                        fontSize: 16,
                        fontWeight: currentPath === path ? 500 : 400,
                        cursor: "pointer",
                        textAlign: "left",
                        marginBottom: 4,
                      }}
                    >
                      {text}
                    </button>
                  ))}
                </div>

                {/* Preferences Section */}
                <div
                  style={{
                    borderTop: `1px solid ${t().border}`,
                    paddingTop: 24,
                    marginBottom: 24,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: t().textDim,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      marginBottom: 12,
                      paddingLeft: 4,
                    }}
                  >
                    Preferences
                  </div>

                  {/* Theme Toggle */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 10px",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ color: t().textMuted, fontSize: 16 }}>
                      Theme
                    </span>
                    <ThemeToggle />
                  </div>

                  {/* Obfuscate Toggle */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ color: t().textMuted, fontSize: 16 }}>
                      Obfuscate IDs
                    </span>
                    <input
                      type="checkbox"
                      checked={obfuscateIdsSignal.value}
                      onChange={(e) => {
                        obfuscateIdsSignal.value = e.currentTarget.checked;
                      }}
                    />
                  </label>
                </div>

                {/* Account Section - pushed to bottom */}
                <div
                  style={{
                    borderTop: `1px solid ${t().border}`,
                    paddingTop: 24,
                    marginTop: "auto",
                  }}
                >
                  {sessionSig.value?.email
                    ? (
                      <>
                        <div
                          style={{
                            fontSize: 11,
                            color: t().textDim,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            marginBottom: 12,
                            paddingLeft: 4,
                          }}
                        >
                          Account
                        </div>
                        <div
                          style={{
                            padding: "12px 10px",
                            fontSize: 14,
                            color: t().textDim,
                            marginBottom: 8,
                            wordBreak: "break-all",
                          }}
                        >
                          {sessionSig.value.email}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            auth.signOut();
                            route("/");
                            setMenuOpen(false);
                          }}
                          style={{
                            display: "block",
                            width: "100%",
                            background: "transparent",
                            color: t().textMuted,
                            border: "none",
                            borderRadius: 4,
                            padding: "12px 10px",
                            fontSize: 16,
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          Sign out
                        </button>
                      </>
                    )
                    : (
                      <button
                        type="button"
                        onClick={() => {
                          route("/groups");
                          setMenuOpen(false);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          background: "transparent",
                          color: t().textMuted,
                          border: "none",
                          borderRadius: 4,
                          padding: "12px 10px",
                          fontSize: 16,
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        Sign in
                      </button>
                    )}
                </div>
              </div>
            </>
          )}
        </>
      )}
      {!isMobile && (
        <>
          {navLinks}
          {userSection}
        </>
      )}
    </div>
  );
};

const WithLogin =
  <P extends object>(Component: ComponentType<P>) => (props: P) => {
    const { isLoading, user, error } = useAuth();
    if (user) sessionSig.value = user;
    if (isLoading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100vw",
            position: "fixed",
            top: 0,
            left: 0,
            backgroundColor: t().pageBg,
          }}
        >
          <ClipLoader color={primaryColor()} />
        </div>
      );
    }
    if (error) return <ErrorMessage error={error} />;
    return user ? <Component {...props} /> : <Login />;
  };

const wrapperStyle = {
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flexGrow: 1,
  width: "100%",
  height: "100vh", // Ensure wrapper fills viewport
  minHeight: 0, // Prevent flex children from growing unexpectedly
};

export const WithHeader =
  <P extends object>(Component: ComponentType<P>) => (props: P) => {
    return (
      <div style={wrapperStyle}>
        <Header />
        <div style={{ ...wrapperStyle, overflow: "auto", height: "auto" }}>
          <Component {...props} />
        </div>
      </div>
    );
  };

const ConversationPage = ({ id }: { id: string }) => (
  <PageContainer fullWidth>
    <ConversationComponent id={id} />
  </PageContainer>
);

export const conversationsRoute = "/conversations";

export const routes = [
  { path: "/", component: WithHeader(LandingPage) },
  {
    path: conversationsRoute,
    component: WithLogin(WithHeader(ConversationsView)),
    text: "Conversations",
  },
  {
    path: "/groups",
    component: WithLogin(WithHeader(GroupsPage)),
    text: "Groups",
  },
  {
    path: "/admin",
    component: WithLogin(WithHeader(AdminPanel)),
    text: "Accounts",
  },
  {
    path: "/snapshots",
    component: WithLogin(WithHeader(SnapshotsView)),
    text: "Snapshots",
  },
  {
    path: "/checkups",
    component: WithLogin(WithHeader(CheckupsPage)),
    text: "Checkups",
  },
  {
    path: "/reports",
    component: WithLogin(WithHeader(ReportsPage)),
  },
  {
    path: "/settings",
    component: WithLogin(WithHeader(SettingsPage)),
    text: "Settings",
  },
  { path: "/docs", component: WithHeader(DocsPage), text: "Docs" },
  { path: "/terms", component: WithHeader(TermsOfService) },
  { path: "/privacy", component: WithHeader(PrivacyPolicy) },
  {
    path: "/conversation/:id",
    component: WithLogin(WithHeader(ConversationPage)),
  },
  {
    path: "/snapshot/:id",
    component: ({ id }: { id: string }) => <SnapshotPage id={id} />,
  },
];
